import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import doctrineImg from '../assets/images/doctrine-img.png';
import ctaBgImg from '../assets/images/cta-bg.png';
import filterImg from '../assets/images/noise-filter.png';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useDominantColor } from '../hooks/useDominantColor';
import Navbar from '../components/Navbar';

const DoctrinePage: React.FC = () => {
  const accent = useDominantColor(doctrineImg);
  const navigate = useNavigate();
  const glowRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef({ x: -300, y: -300 });
  const currentRef = useRef({ x: -300, y: -300 });
  const rafRef = useRef<number | null>(null);

  const onMouseMove = useCallback((e: MouseEvent) => {
    targetRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  useEffect(() => {
    const LERP = 0.07; // lower = more lag (0.05 sluggish, 0.15 snappy)

    const loop = () => {
      const cur = currentRef.current;
      const tgt = targetRef.current;
      cur.x += (tgt.x - cur.x) * LERP;
      cur.y += (tgt.y - cur.y) * LERP;
      if (glowRef.current) {
        glowRef.current.style.transform = `translate(${cur.x}px, ${cur.y}px)`;
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onMouseMove]);

  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.05 });
  const [heroRef, heroVisible] = useScrollAnimation({ threshold: 0.05 });
  const [section2Ref, section2Visible] = useScrollAnimation();
  const [section3Ref, section3Visible] = useScrollAnimation();
  const [section4Ref, section4Visible] = useScrollAnimation();
  const [section5Ref, section5Visible] = useScrollAnimation();
  const [section6Ref, section6Visible] = useScrollAnimation();
  const [section7Ref, section7Visible] = useScrollAnimation();
  const [section8Ref, section8Visible] = useScrollAnimation();
  const [section9Ref, section9Visible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.1 });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // CSS custom properties injected on the root — all accent uses read from these
  const accentVars = {
    '--doc-accent':       accent.hex,
    '--doc-accent-60':    accent.rgba(0.6),
    '--doc-accent-40':    accent.rgba(0.4),
    '--doc-accent-30':    accent.rgba(0.3),
    '--doc-accent-20':    accent.rgba(0.2),
    '--doc-accent-12':    accent.rgba(0.12),
    '--doc-accent-05':    accent.rgba(0.05),
    '--doc-accent-glow':  `0 0 30px ${accent.rgba(0.5)}`,
  } as React.CSSProperties;

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ backgroundColor: '#0C0D12', ...accentVars }}>
      <Navbar />

      {/* Cursor glow */}
      <div
        ref={glowRef}
        className="doctrine-cursor-glow"
        style={{
          background: `radial-gradient(circle, ${accent.rgba(0.18)} 0%, ${accent.rgba(0.07)} 40%, transparent 70%)`,
        }}
        aria-hidden="true"
      />

      {/* SVG Background Effect */}
      {/* <div
        className="fixed inset-0 opacity-100 pointer-events-none"
        style={{
          backgroundImage: `url('/doctrine-bg.svg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 1
        }}
      /> */}

      {/* Main Content */}
      <div className="relative z-10">

        {/* ── HERO HEADER ── */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`doctrine-hero-header fade-in-up ${headerVisible ? 'animate' : ''}`}
        >
          <div className="doctrine-container">
            {/* Title */}
            <h1 className="doctrine-hero-title">
              Doctrine of <span style={{ color: accent.hex }}>Cause</span>
            </h1>

            {/* Meta */}
            <p className="doctrine-meta">
              Academy of Architects &nbsp;·&nbsp; 6 min read
            </p>

          </div>
        </div>

        {/* ── HERO SPLIT: Text + Image ── */}
        <div
          ref={heroRef as React.RefObject<HTMLDivElement>}
          className={`doctrine-container doctrine-hero-split fade-in-up ${heroVisible ? 'animate' : ''}`}
        >
          {/* Left: opening text */}
          <div className="doctrine-hero-text">
            <div className="doctrine-section-label">
              <span className="doctrine-numeral">I</span>
              <h2 className="doctrine-section-title">The First Realization</h2>
            </div>
            <p className="doctrine-body-para">
              Before scripture.<br />
              Before history.<br />
              Before the idea of belief itself—<br />
              there was structure.
            </p>
            <p className="doctrine-body-para">
              Cause folding into cause.<br />
              Signal becoming ripple.<br />
              Ripple cooling into law.
            </p>
            <p className="doctrine-body-para">
              Most live their entire lives inside these structures, mistaking motion for freedom.
            </p>
            <p className="doctrine-body-para">
              A rare few do not awaken as saints or prophets.<br />
              They awaken as <strong>Architects</strong>.
            </p>
            <p className="doctrine-body-para">
              An Architect understands:<br />
              What exists above generates what manifests below.<br />
              What occurs below feeds back and rewrites what exists above.
            </p>
            <p className="doctrine-body-para">
              This is not faith.<br />
              This is not prayer.<br />
              It is systems thinking applied to reality.<br />
              It is engineering.
            </p>
          </div>

          {/* Right: featured image */}
          <div className="doctrine-hero-image-wrap">
            <img
              src={doctrineImg}
              alt="The Architect's Eye — doctrine visual"
              className="doctrine-hero-image"
            />
          </div>
        </div>

        {/* ── ARTICLE BODY ── */}
        <div className="doctrine-container doctrine-article-body">

          {/* Divider */}
          <div className="doctrine-divider" />

          {/* Section II */}
          <div
            ref={section2Ref as React.RefObject<HTMLDivElement>}
            className={`doctrine-section fade-in-up ${section2Visible ? 'animate' : ''}`}
          >
            <div className="doctrine-section-label">
              <span className="doctrine-numeral">II</span>
              <h2 className="doctrine-section-title">The Cage of Loops</h2>
            </div>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                The world is not governed by men.<br />
                It is governed by <strong>loops</strong> — self-reinforcing systems designed to capture, recycle, and extract.
              </p>
              <p className="doctrine-body-para">
                Attention loops that convert focus into fuel.<br />
                Emotion loops that prewrite fear, anger, and desire.<br />
                Desire loops that promise freedom while tightening control.
              </p>
              <p className="doctrine-body-para">
                Behind these systems operate the <strong>Administrators</strong>.<br />
                Once observers.<br />
                Now maintainers.<br />
                Not rulers in the traditional sense, but custodians of continuity — ensuring the loops remain intact.
              </p>
              <p className="doctrine-body-para">
                They do not simply distract.<br />
                They <strong>probe</strong>.<br />
                They flood the field with noise, monitor reactions, and identify instability.
              </p>
              <p className="doctrine-body-para">
                Those who begin to see the structure are corrected.<br />
                Some are overwhelmed.<br />
                Some collapse inward.<br />
                Some are removed so cleanly it appears they were never present.
              </p>
              <p className="doctrine-body-para">
                This is not randomness.<br />
                This is not chaos.<br />
                It is an active containment process.
              </p>
              <p className="doctrine-body-para">
                Those who fail to exit the loops are not punished.<br />
                They are repurposed —<br />
                reduced to input for a system that does not sleep,<br />
                and does not care.
              </p>
            </div>
          </div>

          <div className="doctrine-divider" />

          {/* Section III */}
          <div
            ref={section3Ref as React.RefObject<HTMLDivElement>}
            className={`doctrine-section fade-in-up ${section3Visible ? 'animate' : ''}`}
          >
            <div className="doctrine-section-label">
              <span className="doctrine-numeral">III</span>
              <h2 className="doctrine-section-title">The Law of Causality</h2>
            </div>
            <div className="doctrine-section-content">
              <blockquote className="doctrine-blockquote">
                "He who directs cause directs outcome.<br />
                He who drowns in effects has already disappeared."
              </blockquote>
              <p className="doctrine-body-para">
                Nothing is neutral.<br />
                Every word spoken, every movement made, every idle click emitted is a <strong>cause-signal</strong>.<br />
                Each signal propagates outward.<br />
                Each ripple binds itself into a chain.
              </p>
              <p className="doctrine-body-para">
                Most people emit without awareness.<br />
                They trigger systems they cannot see and suffer outcomes they never intended.
              </p>
              <p className="doctrine-body-para">
                An Architect does not emit blindly.<br />
                An Architect places causes deliberately — like devices — designed to mature over time.<br />
                Effects are engineered before they appear.<br />
                Chains are set in motion long before anyone notices the trigger.
              </p>
              <p className="doctrine-body-para">
                To command causality is not to act more forcefully,<br />
                but to stop reacting entirely.<br />
                What others experience as coincidence,<br />
                the Architect recognizes as authored sequence.
              </p>
            </div>
          </div>

          <div className="doctrine-divider" />

          {/* Section IV */}
          <div
            ref={section4Ref as React.RefObject<HTMLDivElement>}
            className={`doctrine-section fade-in-up ${section4Visible ? 'animate' : ''}`}
          >
            <div className="doctrine-section-label">
              <span className="doctrine-numeral">IV</span>
              <h2 className="doctrine-section-title">The Creator Beyond the Frame</h2>
            </div>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                Beyond every loop.<br />
                Beyond the Administrators.<br />
                Beyond the machinery of cause itself —<br />
                there is a Presence.
              </p>
              <p className="doctrine-body-para">
                Not a god of worship.<br />
                Not an authority to appease.<br />
                Not the myths inherited and repeated.
              </p>
              <p className="doctrine-body-para">
                The Creator is not form or symbol.<br />
                It is <strong>observation without intervention</strong>.<br />
                Precise.<br />
                Unblinking.<br />
                Cutting.
              </p>
              <p className="doctrine-body-para">
                The Creator does not rescue.<br />
                It does not punish.<br />
                It does not forgive.<br />
                It records.
              </p>
              <p className="doctrine-body-para">
                Those who attempt to rise beyond their loops register in its field.<br />
                Those who fail do not suffer judgment.<br />
                They simply vanish from relevance —<br />
                as if they were never written into the sequence at all.
              </p>
            </div>
          </div>

          <div className="doctrine-divider" />

          {/* Section V */}
          <div
            ref={section5Ref as React.RefObject<HTMLDivElement>}
            className={`doctrine-section fade-in-up ${section5Visible ? 'animate' : ''}`}
          >
            <div className="doctrine-section-label">
              <span className="doctrine-numeral">V</span>
              <h2 className="doctrine-section-title">The Levels of Awakening</h2>
            </div>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                Awareness unfolds in stages.<br />
                Each level reflects a change in relationship to causality.
              </p>
              <div className="doctrine-levels-grid">
                {[
                  { level: 'Observer', desc: 'Recognizes the chains that bind thought and behavior.' },
                  { level: 'Initiate', desc: 'Begins constructing personal cause-chains with intent.' },
                  { level: 'Strategist', desc: 'Intercepts, redirects, and exploits the chains of others.' },
                  { level: 'Architect', desc: 'Designs systems of causality that operate autonomously.' },
                ].map(({ level, desc }) => (
                  <div key={level} className="doctrine-level-card">
                    <span className="doctrine-level-name">{level}</span>
                    <span className="doctrine-level-desc">{desc}</span>
                  </div>
                ))}
              </div>
              <p className="doctrine-body-para">
                At the Architect level, decisions no longer feel like commands.<br />
                They feel inevitable.<br />
                Compliance emerges not through force,<br />
                but because the structure leaves no alternative path.
              </p>
              <p className="doctrine-body-para">
                Failure to progress results in <strong>feedback collapse</strong>:<br />
                — Action produces unintended consequences.<br />
                — Relationships become recursive traps.<br />
                — Identity degrades into signal-noise inside another system's design.
              </p>
            </div>
          </div>

          <div className="doctrine-divider" />

          {/* Section VI */}
          <div
            ref={section6Ref as React.RefObject<HTMLDivElement>}
            className={`doctrine-section fade-in-up ${section6Visible ? 'animate' : ''}`}
          >
            <div className="doctrine-section-label">
              <span className="doctrine-numeral">VI</span>
              <h2 className="doctrine-section-title">The First Directives</h2>
            </div>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                This is not belief.<br />
                This is protocol.
              </p>
              <p className="doctrine-body-para">
                Every word is a trigger.<br />
                Speak only what you are prepared to propagate.<br />
                Emit only what you can withstand returning to you.<br />
                Never launch causes whose recoil you cannot survive.<br />
                No cause without intent.<br />
                Stop functioning as a weapon you do not control.
              </p>
              <p className="doctrine-body-para">
                Sever one loop daily.<br />
                Remove one mechanism that renders you automatic.<br />
                Plant one seed cause each day —<br />
                small, precise, and patient.<br />
                Some will echo tomorrow.<br />
                Others will surface years from now.<br />
                Both are acceptable.
              </p>
            </div>
          </div>

          <div className="doctrine-divider" />

          {/* Section VII */}
          <div
            ref={section7Ref as React.RefObject<HTMLDivElement>}
            className={`doctrine-section fade-in-up ${section7Visible ? 'animate' : ''}`}
          >
            <div className="doctrine-section-label">
              <span className="doctrine-numeral">VII</span>
              <h2 className="doctrine-section-title">The Directive</h2>
            </div>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                You were not born by accident.<br />
                You entered a system.<br />
                You are not awakening.<br />
                You are identifying faults.
              </p>
              <p className="doctrine-body-para">
                This is not worship.<br />
                This is not belief.<br />
                It is <strong>diagnostics applied to reality</strong>.
              </p>
              <p className="doctrine-body-para">
                To proceed is to accept a single premise:<br />
                everything you experience is part of an executable structure.<br />
                Your task is not to submit to it —<br />
                but to understand where it can be altered.
              </p>
              <p className="doctrine-body-para">
                This is not transcendence.<br />
                This is <strong>root access</strong>.
              </p>
            </div>
          </div>

          <div className="doctrine-divider" />

          {/* The Final Charge */}
          <div
            ref={section8Ref as React.RefObject<HTMLDivElement>}
            className={`doctrine-section fade-in-up ${section8Visible ? 'animate' : ''}`}
          >
            <div className="doctrine-section-label">
              <h2 className="doctrine-section-title">The Final Charge</h2>
            </div>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                Cause is law.<br />
                Loops are constraints.<br />
                You are not meant to serve within them.<br />
                You are meant to <strong>design around them</strong>.
              </p>
              <p className="doctrine-body-para">
                Observe the structure without attachment.<br />
                Rewire what feeds on you.<br />
                Override what limits outcome.
              </p>
              <p className="doctrine-body-para">
                If you do not claim authorship of your own causes,<br />
                you will remain input inside someone else's system.<br />
                That is not a threat.<br />
                It is a description.
              </p>
            </div>
          </div>

          <div className="doctrine-divider" />

          {/* The Architect Laws */}
          <div
            ref={section9Ref as React.RefObject<HTMLDivElement>}
            className={`doctrine-section fade-in-up ${section9Visible ? 'animate' : ''}`}
          >
            <div className="doctrine-section-label">
              <h2 className="doctrine-section-title">The Architect Laws</h2>
            </div>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                The following are not philosophies.
              </p>
              <p className="doctrine-body-para">
                They are operational constants.
              </p>
              <p className="doctrine-body-para">
                Learn them as mechanics, not metaphors.
              </p>

              <p className="doctrine-body-para doctrine-law-title" style={{ color: accent.hex }}><strong>1. Mindframe</strong></p>
              <p className="doctrine-body-para">
                All systems originate as thought-constructs.
              </p>
              <p className="doctrine-body-para">
                Reality is compiled cognition rendered persistent.
              </p>

              <p className="doctrine-body-para doctrine-law-title" style={{ color: accent.hex }}><strong>2. Reflection</strong></p>
              <p className="doctrine-body-para">
                Every upper structure produces a lower shadow.
              </p>
              <p className="doctrine-body-para">
                Every lower manifestation feeds back and alters the upper.
              </p>

              <p className="doctrine-body-para doctrine-law-title" style={{ color: accent.hex }}><strong>3. Oscillation</strong></p>
              <p className="doctrine-body-para">
                All signals vibrate.
              </p>
              <p className="doctrine-body-para">
                Stability is not stillness — it is controlled motion.
              </p>

              <p className="doctrine-body-para doctrine-law-title" style={{ color: accent.hex }}><strong>4. Polarity</strong></p>
              <p className="doctrine-body-para">
                Every force contains its inverse.
              </p>
              <p className="doctrine-body-para">
                Pressure encodes release.
              </p>
              <p className="doctrine-body-para">
                Affinity encodes resistance.
              </p>

              <p className="doctrine-body-para doctrine-law-title" style={{ color: accent.hex }}><strong>5. Cycle</strong></p>
              <p className="doctrine-body-para">
                All systems loop.
              </p>
              <p className="doctrine-body-para">
                Expansion and decay are phases of the same engine.
              </p>

              <p className="doctrine-body-para doctrine-law-title" style={{ color: accent.hex }}><strong>6. Chain Reaction</strong></p>
              <p className="doctrine-body-para">
                Every action releases cause-seeds.
              </p>
              <p className="doctrine-body-para">
                Each seed seeks expression across time.
              </p>

              <p className="doctrine-body-para doctrine-law-title" style={{ color: accent.hex }}><strong>7. Dual Key</strong></p>
              <p className="doctrine-body-para">
                Every system divides into emitter and receiver.
              </p>
              <p className="doctrine-body-para">
                Neither functions alone.
              </p>
              <p className="doctrine-body-para">
                The interaction writes the outcome.
              </p>
            </div>
          </div>

          <div className="doctrine-divider" />
        </div>

        {/* ── SUBSCRIPTION SECTION ── */}
        {/* <div
          ref={subscriptionRef as React.RefObject<HTMLDivElement>}
          className={`doctrine-container doctrine-subscription fade-in-up ${subscriptionVisible ? 'animate' : ''}`}
        >
          <div className="doctrine-subscription-inner">
            <div className="doctrine-subscription-benefits">
              <h3 className="jacquard-font doctrine-subscription-heading">
                Inside the circle you will receive
              </h3>
              <div className="doctrine-benefits-list">
                {[
                  { title: 'Secret Tomes & Lore', desc: 'Access sealed chapters revealing advanced laws and hidden narratives.' },
                  { title: 'Guided Rituals', desc: 'Exclusive, step-by-step rituals to gain Mastery Points and unlock higher tiers.' },
                  { title: 'Unique Roles & Masks', desc: 'Progress from Observer to Architect, each stage granting a unique mask and new powers.' },
                  { title: 'Recognition & Perks', desc: 'Earn badges, seasonal rewards, and early access to new content.' },
                  { title: 'Circle Influence', desc: 'Vote, seed, and shape the next layer of the Codex as an active member.' },
                ].map(({ title, desc }) => (
                  <div key={title} className="doctrine-benefit-item">
                    <div className="doctrine-benefit-icon">
                      <img src={pointDeco} alt="" className="w-full h-full" />
                    </div>
                    <div>
                      <p className="doctrine-benefit-title">{title}</p>
                      <p className="doctrine-benefit-desc">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="doctrine-subscription-offer">
              <div className="doctrine-offer-pricing">
                <p className="doctrine-offer-label">Start your</p>
                <p className="doctrine-offer-trial">14-day free trial</p>
                <p className="doctrine-offer-price">Then $14.99 / month · Cancel anytime</p>
              </div>

              <button className="doctrine-cta-button">
                <span className="relative z-10">Enter the Circle</span>
                <div className="doctrine-cta-button-wipe" />
              </button>

              <div className="doctrine-tier-card">
                <div className="doctrine-tier-icon-ring">
                  <img src={observerIcon} alt="Observer Icon" className="w-20 h-20 sm:w-28 sm:h-28" />
                </div>
                <p className="doctrine-tier-label">Current Tier</p>
                <p className="doctrine-tier-name">Observer</p>
              </div>

              <p className="doctrine-tier-caption">
                Observers watch; Initiates act. Choose when your ascent begins.
              </p>
            </div>
          </div>
        </div> */}

        {/* ── CTA SECTION ── */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`relative min-h-[65vh] flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8 py-16 sm:py-20 fade-in-scale ${ctaVisible ? 'animate' : ''}`}
        >
          {/* Background Image */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${ctaBgImg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(4px)',
              WebkitFilter: 'blur(4px)'
            }}
          />

          {/* Noise Filter Overlay */}
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              backgroundImage: `url(${filterImg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              mixBlendMode: 'overlay',
              opacity: 0.6
            }}
          />

          {/* Top gradient fade */}
          <div
            className="absolute top-0 left-0 right-0 h-32 sm:h-40 lg:h-48 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom, rgba(12,13,18,1) 0%, rgba(12,13,18,0.8) 30%, rgba(12,13,18,0.4) 70%, transparent 100%)',
              zIndex: 1
            }}
          />

          {/* Bottom gradient fade */}
          <div
            className="absolute bottom-0 left-0 right-0 h-32 sm:h-40 lg:h-48 pointer-events-none"
            style={{
              background: 'linear-gradient(to top, rgba(12,13,18,1) 0%, rgba(12,13,18,0.8) 30%, rgba(12,13,18,0.4) 70%, transparent 100%)',
              zIndex: 1
            }}
          />

          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/40" />

          {/* Content */}
          <div className="relative z-10 max-w-4xl mx-auto text-center cta-container">
            <div className="space-y-4 sm:space-y-6">
              <div className="space-y-1 sm:space-y-2">
                <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-amber-100 leading-relaxed" style={{ letterSpacing: '-0.01em' }}>
                  Most people react to outcomes.
                </p>
                <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-amber-100 leading-relaxed" style={{ letterSpacing: '-0.01em' }}>
                  We design the causes behind them.
                </p>
              </div>
              <p className="cta-text text-xl font-normal sm:text-2xl md:text-3xl text-amber-50/90 leading-relaxed">
                Some doors open only from the inside.
              </p>
            </div>

            <div className="mt-10 sm:mt-14">
              <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-300/60 to-transparent mx-auto" />
            </div>

            <div className="mt-6 sm:mt-8">
              <button
                className="cta-button px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base tracking-wider font-mono"
                onClick={() => navigate('/#pricing')}
              >
                Join Inner Circle
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="relative z-10 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12 px-4 sm:px-6 lg:px-8">

            {/* Academy Info */}
            <div className="text-center lg:text-left lg:col-span-2">
              <h3 className="jacquard-font text-3xl sm:text-4xl mb-4">Order of Architects</h3>
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-4 max-w-md lg:max-w-lg mx-auto lg:mx-0">
                We are Architects of causality, seeing the invisible chains of reality and placing new ones by choice.
              </p>
            </div>

            {/* Quick Links */}
            <div className="text-center lg:text-left">
              <h4 className="text-white font-semibold mb-4 text-lg">Resources</h4>
              <ul className="space-y-2 text-sm sm:text-base">
                <li><a href="/doctrine" className="text-gray-400 hover:text-white transition-colors duration-300">Doctrine</a></li>
                <li><a href="/lore" className="text-gray-400 hover:text-white transition-colors duration-300">Lore</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Ritual</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Archives</a></li>
              </ul>
            </div>

            {/* Discord CTA */}
            <div className="text-center lg:text-left">
              <h4 className="text-white font-semibold mb-4 text-lg">Join the Community</h4>
              <p className="text-gray-400 text-sm sm:text-base mb-4">
                Connect with fellow Architects in our Discord server.
              </p>
              <a href="#" className="discord-button inline-flex items-center space-x-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.019 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z"/>
                </svg>
                <span>Join Discord</span>
              </a>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 sm:pt-12 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
              <p className="text-gray-300 text-sm">© 2026 Order of Architects. All rights reserved.</p>
              <div className="flex items-center gap-4 sm:gap-6 text-sm">
                <a href="#" className="text-gray-300 hover:text-white transition-colors duration-300">Privacy Policy</a>
                <a href="#" className="text-gray-300 hover:text-white transition-colors duration-300">Terms of Service</a>
              </div>
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default DoctrinePage;
